import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const SelectorContainer = styled.div`
  display: flex;
  align-items: center;
  background: linear-gradient(145deg, #ffffff, #f5f6fa);
  border-radius: 16px;
  padding: 6px 10px;
  box-shadow: 
    4px 4px 8px rgba(0, 0, 0, 0.05),
    -4px -4px 8px rgba(255, 255, 255, 0.8);
  max-width: 280px;
  width: 100%;
  margin: 0 auto 16px;
`;

const MonthButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: #6c7d95;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, color 0.2s;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  
  &:hover {
    color: #4a6fa1;
    background: rgba(255, 255, 255, 0.5);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  &:active {
    transform: scale(0.95);
    background: rgba(0, 0, 0, 0.03);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    box-shadow: none;
    
    &:hover {
      color: #6c7d95;
      background: none;
      transform: none;
    }
  }
  
  svg {
    width: 22px;
    height: 22px;
    fill: currentColor;
  }
`;

const MonthText = styled.div`
  flex: 1;
  text-align: center;
  font-weight: 600;
  font-size: 16px;
  color: #4a6fa1;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  /* Красивое подчеркивание текущего месяца */
  &:after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 40%;
    height: 2px;
    background: linear-gradient(90deg, #6a11cb, #2575fc);
    border-radius: 2px;
    transition: width 0.2s;
  }
  
  &:hover:after {
    width: 60%;
  }
`;

// Иконки для кнопок месяца
const ChevronLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
  </svg>
);

const months = [
  { num: 1, ru: "Январь", en: "January", uk: "Січень", id: "Januari" },
  { num: 2, ru: "Февраль", en: "February", uk: "Лютий", id: "Februari" },
  { num: 3, ru: "Март", en: "March", uk: "Березень", id: "Maret" },
  { num: 4, ru: "Апрель", en: "April", uk: "Квітень", id: "April" },
  { num: 5, ru: "Май", en: "May", uk: "Травень", id: "Mei" },
  { num: 6, ru: "Июнь", en: "June", uk: "Червень", id: "Juni" },
  { num: 7, ru: "Июль", en: "July", uk: "Липень", id: "Juli" },
  { num: 8, ru: "Август", en: "August", uk: "Серпень", id: "Agustus" },
  { num: 9, ru: "Сентябрь", en: "September", uk: "Вересень", id: "September" },
  { num: 10, ru: "Октябрь", en: "October", uk: "Жовтень", id: "Oktober" },
  { num: 11, ru: "Ноябрь", en: "November", uk: "Листопад", id: "November" },
  { num: 12, ru: "Декабрь", en: "December", uk: "Грудень", id: "Desember" }
];

const MonthSelector = ({ currentMonth, onMonthChange, onChangeMonth }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ru';
  const selectorRef = useRef(null);

  // Поддержка обоих типов пропсов для совместимости
  const handleMonthChange = (month) => {
    if (onMonthChange) {
      onMonthChange(month);
    } else if (onChangeMonth) {
      onChangeMonth(month);
    }
    
    // Синхронизируем с localStorage
    localStorage.setItem('selected_month', month.toString());
  };

  // Получаем текущий месяц для проверки возможности навигации вперед
  const now = new Date();
  const currentYear = now.getFullYear();
  const realCurrentMonth = now.getMonth() + 1; // JavaScript месяцы начинаются с 0
  
  // Улучшенная логика - позволяем выбирать включая текущий месяц
  const canNavigateNext = currentMonth < realCurrentMonth;

  // Обработчики изменения месяца
  const handlePrevMonth = () => {
    const newMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    handleMonthChange(newMonth);
  };

  const handleNextMonth = () => {
    if (canNavigateNext) {
      const newMonth = currentMonth === 12 ? 1 : currentMonth + 1;
      handleMonthChange(newMonth);
    }
  };

  // Получаем название месяца из массива месяцев с учетом языка
  const getMonthName = (monthNum) => {
    const monthObj = months.find(m => m.num === monthNum);
    return monthObj ? (monthObj[currentLang] || monthObj.ru) : '';
  };

  // При изменении месяца в localStorage, обновляем компонент
  useEffect(() => {
    const savedMonth = localStorage.getItem('selected_month');
    if (savedMonth && parseInt(savedMonth) !== currentMonth) {
      const parsedMonth = parseInt(savedMonth);
      if (parsedMonth >= 1 && parsedMonth <= 12) {
        handleMonthChange(parsedMonth);
      }
    } else if (currentMonth) {
      localStorage.setItem('selected_month', currentMonth.toString());
    }
  }, []);

  return (
    <SelectorContainer ref={selectorRef}>
      <MonthButton onClick={handlePrevMonth} aria-label={t('previous_month')}>
        <ChevronLeftIcon />
      </MonthButton>
      
      <MonthText>
        {getMonthName(currentMonth)}
      </MonthText>
      
      <MonthButton 
        onClick={handleNextMonth} 
        disabled={!canNavigateNext}
        aria-label={t('next_month')}
      >
        <ChevronRightIcon />
      </MonthButton>
    </SelectorContainer>
  );
};

export default MonthSelector;